import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, apiClient } from './client'
import { v2, v2Unscoped, v2api } from './v2'
import type { Staff, Store, AuthTokenResponse, SelectStoreResponse, ClerkTokenResponse, RefreshTokenResponse } from '@/types'
import { useSessionStore } from '@/stores/sessionStore'
import { useUIStore } from '@/stores/uiStore'
import { broadcastBootstrapInvalidated } from '@/lib/sessionChannel'
import { UNLIMITED_DISCOUNT_PERCENT } from '@/lib/discountLimit'


export const authKeys = {
  all: ['auth'] as const,
  session: () => [...authKeys.all, 'session'] as const,
  stores: () => [...authKeys.all, 'stores'] as const,
}


// ---- Manager/Store Login ----

interface AuthLoginParams {
  auth_type: 'manager' | 'store'
  username?: string
  password?: string
  api_key?: string
  api_password?: string
}

// Backend strict responses spread dict fields at top level: {success: true, field1: ..., field2: ...}
type StrictResponse<T> = { success: boolean; error?: string } & T

export function useAuthLogin() {
  return useMutation({
    // Sign-in is its own connectivity check: never park it behind
    // onlineManager. A paused mutation never settles, so a stale browser
    // "offline" would spin Sign In forever with no request and no error.
    // Really offline, it fails fast and the form shows the error instead.
    networkMode: 'always',
    mutationFn: async (params: AuthLoginParams): Promise<AuthTokenResponse> => {
      const response = await apiClient.post<StrictResponse<AuthTokenResponse>>(
        '/ht/api/v2/auth/token',
        params
      )
      const body = response.data
      if (!body.success) {
        throw new Error(body.error || 'Login failed')
      }
      return body
    },
    onSuccess: (data) => {
      const { setAuth, setUserToken } = useSessionStore.getState()

      // Manager flow: response carries `user_token` + `accessible_stores`.
      // Stash both in memory and let /select-store handle the picker +
      // /v2/auth/select-store exchange. We do NOT call setAuth here —
      // there's no session_token yet.
      if (data.auth_type === 'manager' && data.user_token) {
        setUserToken(data.user_token, data.accessible_stores ?? [], data.username ?? null)
        return
      }

      // Store / API-key flow: single-store, BE returns session_token +
      // refresh_token + store_id directly. Same flow as before.
      if (data.session_token && data.refresh_token && data.store_id) {
        const store: Store = {
          id: data.store_id,
          name: `Store ${data.store_id}`,
          address: null,
          city: null,
          province: null,
          postal_code: null,
          phone: null,
          timezone: 'America/Toronto',
          is_cannabis: true,
          default_tax_rate: 13,
        }
        setAuth(data.session_token, data.refresh_token, data.auth_type, store, data.username ?? null)
        useSessionStore.getState().setDisplayLogo(data.display_logo ?? null)
        return
      }

      // Neither shape matched — defensive guard. Should never hit
      // in practice; surface as a thrown error so the cashier sees
      // something instead of a silent dead-end.
      throw new Error('Login response missing both user_token and session_token.')
    },
  })
}


// Manager flow leg 2: exchange the in-memory `user_token` for a real
// session_token by picking a store. Response shape mirrors today's
// single-store login response. Authorization header is the user_token
// directly — the apiClient interceptor respects caller-supplied
// Authorization headers (see client.ts request interceptor).
export function useSelectStore() {
  return useMutation({
    networkMode: 'always', // part of sign-in; see useAuthLogin
    mutationFn: async (params: { store: Store }): Promise<SelectStoreResponse> => {
      const { userToken } = useSessionStore.getState()
      if (!userToken) {
        throw new Error('Session expired. Please sign in again.')
      }
      const response = await apiClient.post<StrictResponse<SelectStoreResponse>>(
        '/ht/api/v2/auth/select-store',
        { store_id: params.store.id },
        { headers: { Authorization: `Bearer ${userToken}` } },
      )
      const body = response.data
      if (!body.success) {
        throw new Error(body.error || 'Store selection failed')
      }
      return body
    },
    onSuccess: (data, variables) => {
      const { setAuth } = useSessionStore.getState()
      // Prefer the rich Store object the picker passed in (carries
      // name / address / city from /v2/auth/token's accessible_stores)
      // over the BE's minimal `{store_id}` echo. The BE's
      // useBackfillSelectedStore hook will fill in any missing fields
      // once /select-register mounts.
      setAuth(
        data.session_token,
        data.refresh_token,
        'manager',
        variables.store,
        data.username ?? null,
      )
      useSessionStore.getState().setDisplayLogo(data.display_logo ?? null)
    },
  })
}


// Mid-session store switch for managers. The user_token is gone by
// this point (single-use), so we hit /v2/auth/switch-store with the
// current session_token. BE issues a new session_token bound to the
// new store; clerk session is wiped because registers belong to a
// store and the BE expects /auth/clerk to run again at the new
// register. API-key sessions get a 403 and are gated out at the call
// site (auth_type === 'manager' only).
export function useSwitchStore() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { store: Store }): Promise<SelectStoreResponse> => {
      const response = await apiClient.post<StrictResponse<SelectStoreResponse>>(
        '/ht/api/v2/auth/switch-store',
        { store_id: params.store.id },
      )
      const body = response.data
      if (!body.success) {
        throw new Error(body.error || 'Store switch failed')
      }
      return body
    },
    onSuccess: (data, variables) => {
      const { setAuth, clearClerkAuth } = useSessionStore.getState()
      clearClerkAuth()
      setAuth(
        data.session_token,
        data.refresh_token,
        'manager',
        variables.store,
        data.username ?? null,
      )
      useSessionStore.getState().setDisplayLogo(data.display_logo ?? null)
      // Wipe per-store caches (registers, products, customers, …) so
      // the next screen doesn't briefly show the old store's data.
      queryClient.clear()
    },
  })
}


// On-demand refresh of the manager's accessible-store list. /v2/auth/stores
// accepts either user_token or session_token, so it works both during
// initial login and mid-session before opening the switcher.
interface AccessibleStoresResponse {
  // BE returns `accessible_stores` to mirror the /auth/token shape;
  // `stores` is accepted defensively in case the contract drifts.
  accessible_stores?: Store[]
  stores?: Store[]
}
export function useAccessibleStores(enabled: boolean = true) {
  const sessionToken = useSessionStore((s) => s.sessionToken)
  const userToken = useSessionStore((s) => s.userToken)
  const authType = useSessionStore((s) => s.authType)

  return useQuery({
    queryKey: [...authKeys.all, 'accessible_stores'],
    queryFn: async (): Promise<Store[]> => {
      const data = await apiClient.get<StrictResponse<AccessibleStoresResponse>>(
        '/ht/api/v2/auth/stores',
      )
      if (!data.data.success) {
        throw new Error(data.data.error || 'Failed to load stores')
      }
      return data.data.accessible_stores ?? data.data.stores ?? []
    },
    enabled: enabled && authType === 'manager' && !!(sessionToken || userToken),
    // Stale-while-revalidate: every /select-store mount refetches so an
    // admin revoking access mid-session is reflected, while the
    // previously-cached list keeps rendering during the in-flight call
    // (no spinner on the common case).
    staleTime: 0,
    refetchOnMount: 'always',
  })
}


// ---- Clerk PIN Login ----

interface ClerkLoginParams {
  pin: string
  register_id: number
}

/** Discount permissions off the clerk payload. Permissive when the backend
 *  omits them (older deploy): the POS then just can't pre-check, and the
 *  backend's 409 still opens the approval flow from the cart banner. */
export function clerkDiscountFields(
  data: Pick<ClerkTokenResponse, 'can_discounts' | 'discount_limit_percent' | 'can_override_discount_limit'>,
): Pick<Staff, 'canDiscounts' | 'discountLimitPercent' | 'canOverrideDiscountLimit'> {
  const raw = data.discount_limit_percent
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw ?? ''))
  return {
    canDiscounts: data.can_discounts !== false,
    discountLimitPercent: Number.isFinite(n) ? n : UNLIMITED_DISCOUNT_PERCENT,
    canOverrideDiscountLimit: data.can_override_discount_limit !== false,
  }
}

export function useClerkLogin() {
  const queryClient = useQueryClient()

  return useMutation({
    networkMode: 'always', // part of sign-in; see useAuthLogin
    mutationFn: async (params: ClerkLoginParams): Promise<ClerkTokenResponse> => {
      const response = await apiClient.post<StrictResponse<ClerkTokenResponse>>(
        '/ht/api/v2/auth/clerk',
        params
      )
      const body = response.data
      if (!body.success) {
        throw new Error(body.error || 'Invalid PIN')
      }
      return body
    },
    onSuccess: (data, variables) => {
      const { setClerkAuth, selectedRegister, selectedStore } = useSessionStore.getState()

      const staff: Staff = {
        id: data.clerk_id,
        first_name: '',
        last_name: '',
        full_name: data.username,
        pin: '',
        email: null,
        // POS roles/permission lists aren't on the clerk payload; the
        // discount fields below are the only permissions the POS reads.
        role: 'clerk',
        permissions: [],
        stores: [data.store_id],
        is_active: true,
        ...clerkDiscountFields(data),
      }

      // Use the existing register object if we already have it, otherwise build one
      const register = selectedRegister?.id === variables.register_id
        ? selectedRegister
        : {
            id: variables.register_id,
            store_id: data.store_id,
            store_name: selectedStore?.name || `Store ${data.store_id}`,
            name: `Register ${variables.register_id}`,
            terminal_id: String(variables.register_id),
            is_active: true,
            last_activity: null,
            local_printing_enabled: false,
          }

      setClerkAuth(data.clerk_token, staff, register)
      useSessionStore.getState().setDisplayLogo(data.display_logo ?? null)

      // Any successful PIN sign-in resolves a pending clerk re-auth:
      // clear the overlay flag centrally so a flag raised during a
      // logout/login transition can't survive into the fresh clerk's
      // session and show a stale "PIN sign-in needed" overlay on /pos.
      useUIStore.getState().clearClerkReauth()

      // A new clerk is starting their shift on this register. The cash-modal
      // flags live in uiStore, which is in-memory and is NOT reset on logout.
      // On terminals that don't hard-reload between shifts (the norm), a Cash
      // Out the previous clerk left open would otherwise re-render the instant
      // this clerk lands on /pos — the reported "POS opened the cash-out
      // screen at login" bug. Reset both so the new session starts with a
      // clean modal slate; POSLayout re-prompts Cash In on its own when this
      // shift genuinely needs it. (Also reset on teardown in clearClerkAuth.)
      useUIStore.getState().closeCashModals()

      // Force-invalidate ALL queries on clerk PIN, not just bootstrap.
      // Reason: the prior clerk's logout calls `queryClient.clear()`,
      // but in-flight register-scoped requests (axios isn't wired to
      // AbortSignal, so cancelQueries can't actually abort them) keep
      // running and resolve with 403/401 against the now-dead clerk
      // session. Those 403s land in the response interceptor's refresh
      // flow; if a fresh `useInfiniteProducts` from the second /pos
      // mount fires concurrently, its retry can get parked in the
      // refresh-subscriber queue and orphaned when that queue is reset
      // on refresh-failure — leaving the products grid stuck on its
      // spinner. Invalidating everything here forces those refetches
      // to be authoritative and bypasses any zombie in-flight state.
      // Also covers the original bootstrap-refresh-on-shift-change
      // intent (discount schedules, display_logo, etc.).
      queryClient.invalidateQueries()
      broadcastBootstrapInvalidated()
    },
  })
}


// ---- Token Refresh ----

export function useRefreshToken() {
  return useMutation({
    mutationFn: async (): Promise<RefreshTokenResponse> => {
      // V2AuthRefreshHandler -> PosAuthRefreshHandler reads `refresh_token`
      // from the body; an empty body 401s with "Refresh token is required."
      const { refreshToken } = useSessionStore.getState()
      const response = await apiClient.post<StrictResponse<RefreshTokenResponse>>(
        '/ht/api/v2/auth/refresh',
        { refresh_token: refreshToken },
      )
      const body = response.data
      if (!body.success) {
        throw new Error(body.error || 'Refresh failed')
      }
      return body
    },
    onSuccess: (data) => {
      const { refreshToken } = useSessionStore.getState()
      useSessionStore.getState().setSessionTokens(
        data.session_token,
        data.refresh_token ?? refreshToken ?? '',
      )
      // `!== undefined` so a refresh response from an older BE deploy
      // (no display_logo field) doesn't wipe the known-good logo from
      // the prior auth event.
      if (data.display_logo !== undefined) {
        useSessionStore.getState().setDisplayLogo(data.display_logo ?? null)
      }
    },
  })
}


// ---- Session Status (validates current session via any authenticated endpoint) ----

interface StatusResponse {
  orders_created_today?: number
  po_badge_count?: number
  so_badge_count?: number
  tr_badge_count?: number
  no_limits?: boolean
}

interface SessionResponse {
  isAuthenticated: boolean
  staff: Staff | null
  store: Store | null
  status?: StatusResponse
}

export function useSession() {
  const { backendUrl, sessionToken } = useSessionStore()

  return useQuery({
    queryKey: authKeys.session(),
    queryFn: async (): Promise<SessionResponse> => {
      try {
        const data = await api.get<StatusResponse>('/ht/api/status')
        return {
          isAuthenticated: true,
          staff: null,
          store: null,
          status: data,
        }
      } catch {
        return { isAuthenticated: false, staff: null, store: null }
      }
    },
    staleTime: 1000 * 60 * 5,
    retry: false,
    enabled: !!backendUrl && !!sessionToken,
  })
}


// ---- Stores List (for store selection if needed) ----

interface StoresResponse {
  stores: Store[]
}

export function useStores() {
  const { backendUrl, sessionToken } = useSessionStore()

  return useQuery({
    queryKey: authKeys.stores(),
    queryFn: async (): Promise<Store[]> => {
      const data = await api.get<StoresResponse>('/ht/api/select-store')
      return data.stores
    },
    staleTime: 1000 * 60 * 30,
    enabled: !!backendUrl && !!sessionToken,
  })
}


// ---- Logout ----

// POST v2('/clerk/logout') — drop the clerk session on the backend, then
// clear local clerk state. Best-effort: if the backend call fails (e.g.
// already-expired token), still clear locally.
export function useClerkLogout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (): Promise<void> => {
      try {
        await v2api.post(v2('/clerk/logout'), {})
      } catch (err) {
        console.warn('[useClerkLogout] backend call failed; clearing local state anyway', err)
      }
      useSessionStore.getState().clearClerkAuth()
    },
    onSuccess: () => {
      queryClient.clear()
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (mode: 'clerk' | 'full' = 'clerk'): Promise<void> => {
      const { clearClerkAuth, clearSession, clerkToken } = useSessionStore.getState()
      if (mode === 'full') {
        if (clerkToken) {
          try {
            await v2api.post(v2('/clerk/logout'), {})
          } catch (err) {
            console.warn('[useLogout] clerk-logout call failed; continuing with full logout', err)
          }
        }
        clearSession()
      } else {
        if (clerkToken) {
          try {
            await v2api.post(v2('/clerk/logout'), {})
          } catch (err) {
            console.warn('[useLogout] clerk-logout call failed; clearing local state anyway', err)
          }
        }
        clearClerkAuth()
      }
    },
    onSuccess: () => {
      queryClient.clear()
    },
  })
}


// ---- Clock In/Out ----

// ---- Clock In ----
// Backend uses pos_cash_in which records the opening cash count alongside the clock-in.
// GET to check current state, POST to submit the clock-in with cash count.
// hypnotoad/handlers/api/staff/clock_in.py

interface CashInData {
  moneys: Array<{ description: string; value: number; qty: number }>
}

interface CashInResponse {
  clock_in: Record<string, unknown>
  personnel_in: Record<string, unknown>
  editable: boolean
  cash_in: CashInData | null
}

export function useGetCashInStatus(enabled: boolean = true) {
  return useQuery({
    queryKey: [...authKeys.all, 'cash_in_status'],
    queryFn: async (): Promise<CashInResponse> => {
      return v2api.get<CashInResponse>(v2('/staff/cash-in'))
    },
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useClockIn() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params?: {
      cashIn?: CashInData
      totalIn?: number
    }): Promise<CashInResponse> => {
      return v2api.post<CashInResponse>(v2('/staff/cash-in'), {
        cash_in: params?.cashIn ?? { moneys: [] },
        total_in: params?.totalIn ?? 0,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.all })
    },
  })
}

// ---- Clock Out ----
// GET returns shift summary (totals, expected cash), POST submits the closing count.
// hypnotoad/handlers/api/staff/clock_out.py

interface CashOutStatus {
  editable: boolean
  start: string
  finish: string | null
  cash: string
  visa: string
  debit: string
  master_card: string
  amex: string
  total: string
  taxes: string
  drop_cash: string
  total_cards: string
  total_in?: string
  cash_in: CashInData | null
  cash_out: Record<string, unknown> | null
}

export function useGetCashOutStatus(enabled: boolean = true) {
  return useQuery({
    queryKey: [...authKeys.all, 'cash_out_status'],
    queryFn: async (): Promise<CashOutStatus> => {
      return v2api.get<CashOutStatus>(v2('/staff/cash-out'))
    },
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useClockOut() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params?: {
      cashOut?: CashInData
      totalOut?: number
      visaNew?: number
      debitNew?: number
      discoverNew?: number
      masterCardNew?: number
      amexNew?: number
      totalCardsNew?: number
      varianceNote?: string
    }): Promise<{ clock_out: Record<string, unknown> }> => {
      const payload = {
        cash_out: params?.cashOut ?? { moneys: [] },
        total_out: params?.totalOut ?? 0,
        visa_new: params?.visaNew ?? 0,
        debit_new: params?.debitNew ?? 0,
        discover_new: params?.discoverNew ?? 0,
        master_card_new: params?.masterCardNew ?? 0,
        amex_new: params?.amexNew ?? 0,
        total_cards_new: params?.totalCardsNew ?? 0,
        variance_note: params?.varianceNote ?? '',
      }
      return v2api.post<{ clock_out: Record<string, unknown> }>(v2('/staff/cash-out'), payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.all })
    },
    onError: (error) => {
      // Log the failure shape only — payload (cash counts, variance
      // note) is intentionally omitted to keep production consoles
      // free of cash-management data.
      if (import.meta.env.DEV) {
        console.error('[useClockOut] Mutation failed:', error)
      }
    },
  })
}

// ---- Cash Out Print ----
// POST v2('/staff/cash-out/print') — mirrors portal's pos_cash_out/print handler.
//
// Two paths, same as receipt printing:
//
//   * Local-print registers (local_printing_enabled=true): the server can't
//     reach the cashier's localhost:9999 printer via its own queue, so the
//     BE must return the buffer in the response body and the FE relays it to
//     the local Windows service. The BE needs to include the same
//     {buffer, buffer2, binary, type} shape that /printable-buffer returns.
//
//   * Server-side registers: the BE prints via its own queue as today.
//     No buffer relay needed.
//
// If local_printing_enabled is true but the BE hasn't yet returned a buffer
// (i.e. the backend hasn't added the buffer-return yet), the hook still
// resolves without error — the server attempted its queue path, which silently
// fails for local-only printers. No regression from current behaviour.
export function usePrintCashOutReport() {
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>): Promise<{ status: string }> => {
      const { selectedRegister } = useSessionStore.getState()
      const localPrint = !!selectedRegister?.local_printing_enabled

      type PrintResponse = {
        status: string
        buffer?: string
        buffer2?: string | null
        binary?: boolean
        type?: string
      }
      const response = await v2api.post<PrintResponse>(v2('/staff/cash-out/print'), payload)

      // Local-print registers can't be reached from the server's queue.
      // When the BE returns a buffer (Part B of item 15), relay it to the
      // local Windows print service — same pattern as printReceiptViaPreferredPath.
      if (localPrint && response.buffer) {
        const localPrintUrl =
          (import.meta as { env?: { VITE_LOCAL_PRINT_URL?: string } }).env?.VITE_LOCAL_PRINT_URL
          || 'http://localhost:9999/print'
        const ctrl = new AbortController()
        const timer = window.setTimeout(() => ctrl.abort(), 15000)
        try {
          const res = await fetch(localPrintUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain' },
            body: JSON.stringify(response),
            signal: ctrl.signal,
          })
          if (!res.ok) throw new Error(`Local print service returned ${res.status}`)
        } finally {
          window.clearTimeout(timer)
        }
      }

      return { status: response.status }
    },
  })
}

// ---- Manager PIN Check ----
// POST /ht/api/v2/stores/{sid}/check-manager-access  {pin, permission?}
// (store-scoped, not register-scoped). POST with a JSON body so the PIN is
// never in a URL (URLs land in proxy/access logs and in Bugsnag/PostHog
// network breadcrumbs; the legacy GET ?pin= form is still accepted by the
// backend but no longer used here). Answers {success: true, staff: {id,
// display_name}} or {success: false, error}. Without `permission` the BE
// checks the manager/special role only; with it, that permission too.

export interface ManagerAccessStaff {
  id: number
  display_name: string
}

/** Verify a manager PIN. Resolves with the approving staff member; rejects
 *  with an Error carrying the BE's reason ("Wrong PIN", "Operation is not
 *  allowed", ...) so ManagerApprovalDialog can show it inline. */
export async function checkManagerAccess(
  pin: string,
  permission?: string,
  signal?: AbortSignal,
): Promise<ManagerAccessStaff | null> {
  const { selectedStore } = useSessionStore.getState()
  if (!selectedStore?.id) {
    throw new Error('Store must be selected before manager override')
  }
  const res = await v2api.post<{
    success?: boolean
    error?: string
    staff?: { id?: number; display_name?: string | null } | null
  }>(
    v2Unscoped(`/stores/${selectedStore.id}/check-manager-access`),
    permission ? { pin, permission } : { pin },
    signal ? { signal } : undefined,
  )
  // v2api turns a {success:false, error} body into a V2ApiError already;
  // this guards a body without `success` at all.
  if (res?.success !== true) {
    throw new Error(res?.error || 'Wrong PIN')
  }
  // `staff` is absent on an older backend (it answered bare {success}).
  const s = res.staff
  return s && typeof s.id === 'number'
    ? { id: s.id, display_name: s.display_name || 'Manager' }
    : null
}

// ---- Drop Cash ----
// POST v2('/drop-cash')
export function useDropCash() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      amount: number
      cashOut?: CashInData
    }): Promise<{ id: number; amount: number }> => {
      return v2api.post(v2('/drop-cash'), {
        amount: params.amount,
        cash_out: params.cashOut ?? { moneys: [] },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.all })
    },
  })
}
